import { useTranslation } from 'react-i18next';
import type { Client } from '@/api/clients/clientsApi.types';
import { Card } from '@/components/ui/card';
import { formatPhoneNumberForDisplay } from '@/utils/phoneNumbers';

interface ContactCardProps {
  client: Client;
}

export function ContactCard({ client }: ContactCardProps) {
  const { t } = useTranslation('clients');
  const addressLines = [client.addressLine, joinCityLine(client.postalCode, client.city)].filter(
    function keep(line) {
      return line !== null && line.trim().length > 0;
    }
  );

  return (
    <Card>
      <h2 className="text-sm font-bold text-zooindigo mb-3">{t('detail.contact')}</h2>
      <dl className="space-y-2.5 text-sm">
        <Row label={t('form.email')} value={client.email} fallback={t('detail.noEmail')} />
        <Row
          label={t('form.phone')}
          value={client.phone === null ? null : formatPhoneNumberForDisplay(client.phone)}
          fallback={t('detail.noPhone')}
        />
        <div className="flex gap-3">
          <dt className="text-zoomuted w-16 shrink-0">{t('form.addressLine')}</dt>
          <dd className="text-zooindigo font-medium flex-1 min-w-0 break-words">
            {addressLines.length === 0 ? (
              <span className="text-zoomuted font-normal">{t('detail.noAddress')}</span>
            ) : (
              addressLines.map(function renderLine(line) {
                return <div key={line}>{line}</div>;
              })
            )}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

interface RowProps {
  label: string;
  value: string | null;
  fallback: string;
}

function Row({ label, value, fallback }: RowProps) {
  return (
    <div className="flex gap-3">
      <dt className="text-zoomuted w-16 shrink-0">{label}</dt>
      <dd
        className={
          'flex-1 min-w-0 break-words ' +
          (value === null ? 'text-zoomuted' : 'text-zooindigo font-medium')
        }
      >
        {value === null ? fallback : value}
      </dd>
    </div>
  );
}

function joinCityLine(postalCode: string | null, city: string | null): string | null {
  const parts = [postalCode, city].filter(function keep(part) {
    return part !== null && part.trim().length > 0;
  });
  return parts.length === 0 ? null : parts.join(' ');
}
